/**
 * Remove the orphaned service worker the Next.js app left behind.
 *
 * WHAT WENT WRONG
 * ---------------
 * `public/sw.js` was registered by the Next.js build. The Vite SPA that
 * replaced it registers nothing — `grep serviceWorker.register src/` returns
 * no hits — but an UNREGISTERED worker is not an INACTIVE one. Every browser
 * that opened AIM before the port still has `aim-v1` installed and
 * intercepting fetches, and no code in the new app ever updates or removes it.
 * It outlived the application that installed it.
 *
 * Why that is not harmless: its fetch handler ends with a cache-first
 * catch-all —
 *
 *     if (request.method === "GET") {
 *       event.respondWith(caches.match(request).then((c) => c || fetch(request)));
 *     }
 *
 * — which returns a cached response whenever one exists and never
 * revalidates. `/` is precached. Navigations are network-first so an online
 * user usually still gets fresh HTML, which is why this hid for so long, but
 * anything that ever lands in that cache is served from it forever.
 *
 * WHAT THIS DOES
 * --------------
 * Unregisters every worker on this origin and deletes every Cache Storage
 * entry, once, on load. After one visit the browser is clean and this becomes
 * a no-op costing two resolved promises.
 *
 * It deliberately does NOT register a replacement. AIM has no offline
 * requirement on the web — the Android app is where offline lives (it has
 * expo-sqlite and a sync queue) — and a service worker nobody asked for is
 * what produced this bug.
 *
 * Deliberately NOT awaited by startup: a failure here must never keep the app
 * from rendering. Every call is guarded, and `caches`/`serviceWorker` are
 * absent in non-secure contexts and in some private modes.
 */
export function removeLegacyServiceWorker(): void {
  if (typeof navigator === "undefined") return;

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        for (const registration of registrations) {
          // Fire and forget: unregister() resolves false when the worker is
          // already gone, which is the steady state and not an error.
          void registration.unregister();
        }
      })
      .catch(() => {
        /* Blocked by policy or unavailable — nothing to clean up. */
      });
  }

  if (typeof caches !== "undefined") {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      .catch(() => {
        /* Storage unavailable (private mode, blocked site data). */
      });
  }
}
